import { format } from "date-fns";

import { AccountStatus } from "@/generated/model/accountStatus";
import { AcknowledgementStanding } from "@/generated/model/acknowledgementStanding";
import type { EmployeeDirectoryAcknowledgementsDto } from "@/generated/model/employeeDirectoryAcknowledgementsDto";
import type { EmployeeDirectoryEntryDto } from "@/generated/model/employeeDirectoryEntryDto";
import { EmploymentStatus } from "@/generated/model/employmentStatus";
import type { SortDir } from "@/lib/category-display";
import { buildCsv } from "@/lib/csv";
import { formatUkDate } from "@/lib/date-utils";
import {
  accountStatusLabel,
  employmentStatusLabel,
  fullName,
} from "@/lib/people";
import { staffUsername } from "@/lib/staff-username";

export type DirectorySortColumn =
  | "name"
  | "jobTitle"
  | "room"
  | "lineManager"
  | "employmentStatus"
  | "accountStatus"
  | "acknowledgements"
  | "lastLogin";

export type AcknowledgementFilter =
  | "HAS_OVERDUE"
  | "HAS_OUTSTANDING"
  | "UP_TO_DATE"
  | "NONE_SENT";

export interface DirectoryFilters {
  roomId: string | null;
  /** A normalised job title, as jobTitleOptions gives it. */
  jobTitle: string | null;
  employmentStatus: EmploymentStatus | null;
  accountStatus: AccountStatus | null;
  acknowledgements: AcknowledgementFilter | null;
}

export interface DirectoryOption {
  value: string;
  label: string;
}

export const NO_DIRECTORY_FILTERS: DirectoryFilters = {
  roomId: null,
  jobTitle: null,
  employmentStatus: null,
  accountStatus: null,
  acknowledgements: null,
};

/** The bulk PIN endpoint takes at most this many people at once. */
export const BULK_PIN_LIMIT = 200;

const EMPLOYMENT_ORDER = Object.values(EmploymentStatus);
const ACCOUNT_ORDER = Object.values(AccountStatus);
const STANDING_ORDER: AcknowledgementStanding[] = [
  AcknowledgementStanding.OVERDUE,
  AcknowledgementStanding.OUTSTANDING,
  AcknowledgementStanding.UP_TO_DATE,
  AcknowledgementStanding.NONE_SENT,
];

export function employeeName(entry: EmployeeDirectoryEntryDto): string {
  return (
    fullName(entry.firstName, entry.lastName) || staffUsername(entry.email)
  );
}

export function hasActiveFilters(
  filters: DirectoryFilters,
  search: string,
): boolean {
  if (search.trim() !== "") return true;
  return Object.values(filters).some((value) => value !== null);
}

/** "Chef", " chef " and "CHEF" are one job title. */
export function normaliseJobTitle(title: string): string {
  return title.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Job titles in use, each shown in its most common spelling. */
export function jobTitleOptions(
  entries: readonly EmployeeDirectoryEntryDto[],
): DirectoryOption[] {
  const spellings = new Map<string, Map<string, number>>();
  for (const entry of entries) {
    const title = entry.jobTitle?.trim().replace(/\s+/g, " ");
    if (!title) continue;
    const key = normaliseJobTitle(title);
    const counts = spellings.get(key) ?? new Map<string, number>();
    counts.set(title, (counts.get(title) ?? 0) + 1);
    spellings.set(key, counts);
  }
  return [...spellings.entries()]
    .map(([value, counts]) => ({ value, label: mostCommon(counts) }))
    .sort(byLabel);
}

export function roomOptions(
  entries: readonly EmployeeDirectoryEntryDto[],
): DirectoryOption[] {
  const rooms = new Map<string, string>();
  for (const entry of entries) {
    if (entry.room) rooms.set(entry.room.id, entry.room.name);
  }
  return [...rooms.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort(byLabel);
}

export function filterEmployees(
  entries: readonly EmployeeDirectoryEntryDto[],
  filters: DirectoryFilters,
  search: string,
): EmployeeDirectoryEntryDto[] {
  const query = search.trim().toLowerCase();
  return entries.filter((entry) => {
    if (query && !searchText(entry).includes(query)) return false;
    if (filters.roomId && entry.room?.id !== filters.roomId) return false;
    if (filters.jobTitle && !hasJobTitle(entry, filters.jobTitle)) {
      return false;
    }
    if (
      filters.employmentStatus &&
      entry.employmentStatus !== filters.employmentStatus
    ) {
      return false;
    }
    if (
      filters.accountStatus &&
      entry.loginAndSecurity?.accountStatus !== filters.accountStatus
    ) {
      return false;
    }
    if (
      filters.acknowledgements &&
      !matchesAcknowledgements(entry.acknowledgements, filters.acknowledgements)
    ) {
      return false;
    }
    return true;
  });
}

/** Empty values sort last either way; ties fall back to surname, then first name. */
export function sortEmployees(
  entries: readonly EmployeeDirectoryEntryDto[],
  column: DirectorySortColumn,
  dir: SortDir,
): EmployeeDirectoryEntryDto[] {
  const direction = dir === "asc" ? 1 : -1;
  return [...entries].sort((a, b) => {
    const compared = compareValues(
      sortValue(a, column),
      sortValue(b, column),
      direction,
    );
    if (compared !== 0) return compared;
    return compareValues(sortValue(a, "name"), sortValue(b, "name"), 1);
  });
}

export function acknowledgementLabel(
  acknowledgements: EmployeeDirectoryAcknowledgementsDto,
): string {
  switch (acknowledgements.standing) {
    case AcknowledgementStanding.OVERDUE:
      return `${acknowledgements.overdueCount} overdue`;
    case AcknowledgementStanding.OUTSTANDING:
      return `${acknowledgements.outstandingCount} outstanding`;
    case AcknowledgementStanding.UP_TO_DATE:
      return "Up to date";
    default:
      return "None sent";
  }
}

export function acknowledgementVariant(
  standing: AcknowledgementStanding,
): "error" | "warning" | "success" | "neutral" {
  switch (standing) {
    case AcknowledgementStanding.OVERDUE:
      return "error";
    case AcknowledgementStanding.OUTSTANDING:
      return "warning";
    case AcknowledgementStanding.UP_TO_DATE:
      return "success";
    default:
      return "neutral";
  }
}

export function employmentVariant(
  status: EmploymentStatus | null,
): "brand" | "success" | "neutral" {
  if (status === null) return "neutral";
  if (status === EmploymentStatus.LEFT) return "neutral";
  if (status === EmploymentStatus.ON_LEAVE) return "brand";
  return "success";
}

/** The rows on screen as a CSV file, with the login columns only for those who may see them. */
export function directoryCsv(
  entries: readonly EmployeeDirectoryEntryDto[],
  options: { includeLogin: boolean },
): string {
  const header = [
    "Name",
    "Preferred name",
    "Email",
    "Username",
    "Employee ID",
    "Job title",
    "Room",
    "Line manager",
    "Employment status",
    ...(options.includeLogin ? ["Account status", "Last login"] : []),
    "Acknowledgements",
  ];
  const rows = entries.map((entry) => [
    employeeName(entry),
    entry.preferredName ?? "",
    entry.email,
    staffUsername(entry.email),
    entry.employeeId ?? "",
    entry.jobTitle ?? "",
    entry.room?.name ?? "",
    entry.lineManager?.name ?? "",
    entry.employmentStatus ? employmentStatusLabel(entry.employmentStatus) : "",
    ...(options.includeLogin ? loginCells(entry) : []),
    acknowledgementLabel(entry.acknowledgements),
  ]);
  return buildCsv(header, rows);
}

export function directoryCsvFilename(
  nurseryName: string,
  now: Date = new Date(),
): string {
  const slug =
    nurseryName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "nursery";
  return `people-${slug}-${format(now, "yyyy-MM-dd")}.csv`;
}

function loginCells(entry: EmployeeDirectoryEntryDto): string[] {
  const login = entry.loginAndSecurity;
  if (!login) return ["", ""];
  return [
    accountStatusLabel(login.accountStatus),
    login.lastLoginAt
      ? formatUkDate(new Date(login.lastLoginAt), "d MMM yyyy, h:mm a")
      : "",
  ];
}

function searchText(entry: EmployeeDirectoryEntryDto): string {
  return [
    entry.firstName,
    entry.lastName,
    fullName(entry.firstName, entry.lastName),
    entry.preferredName,
    entry.email,
    staffUsername(entry.email),
    entry.employeeId,
    entry.jobTitle,
    entry.room?.name,
  ]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
}

function hasJobTitle(entry: EmployeeDirectoryEntryDto, title: string): boolean {
  if (!entry.jobTitle) return false;
  return normaliseJobTitle(entry.jobTitle) === title;
}

function matchesAcknowledgements(
  acknowledgements: EmployeeDirectoryAcknowledgementsDto,
  filter: AcknowledgementFilter,
): boolean {
  switch (filter) {
    case "HAS_OVERDUE":
      return acknowledgements.standing === AcknowledgementStanding.OVERDUE;
    case "HAS_OUTSTANDING":
      return acknowledgements.outstandingCount > 0;
    case "UP_TO_DATE":
      return acknowledgements.standing === AcknowledgementStanding.UP_TO_DATE;
    default:
      return acknowledgements.standing === AcknowledgementStanding.NONE_SENT;
  }
}

function sortValue(
  entry: EmployeeDirectoryEntryDto,
  column: DirectorySortColumn,
): string | number | null {
  switch (column) {
    case "name":
      return (
        [entry.lastName, entry.firstName].filter(Boolean).join(" ") ||
        staffUsername(entry.email)
      );
    case "jobTitle":
      return entry.jobTitle;
    case "room":
      return entry.room?.name ?? null;
    case "lineManager":
      return entry.lineManager?.name ?? null;
    case "employmentStatus":
      return orderOf(EMPLOYMENT_ORDER, entry.employmentStatus);
    case "accountStatus":
      return orderOf(ACCOUNT_ORDER, entry.loginAndSecurity?.accountStatus);
    case "acknowledgements":
      return orderOf(STANDING_ORDER, entry.acknowledgements.standing);
    case "lastLogin":
      return entry.loginAndSecurity?.lastLoginAt ?? null;
  }
}

function orderOf<T>(order: readonly T[], value: T | null | undefined) {
  if (value === null || value === undefined) return null;
  const index = order.indexOf(value);
  return index === -1 ? null : index;
}

function compareValues(
  a: string | number | null,
  b: string | number | null,
  direction: number,
): number {
  if (a === b) return 0;
  if (a === null || a === "") return 1;
  if (b === null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * direction;
  }
  return (
    String(a).localeCompare(String(b), "en-GB", { sensitivity: "base" }) *
    direction
  );
}

function mostCommon(counts: Map<string, number>): string {
  let best = "";
  let bestCount = 0;
  for (const [spelling, count] of counts) {
    if (count > bestCount) {
      best = spelling;
      bestCount = count;
    }
  }
  return best;
}

function byLabel(a: DirectoryOption, b: DirectoryOption): number {
  return a.label.localeCompare(b.label, "en-GB", { sensitivity: "base" });
}
